import { cn } from "@/lib/utils";

export const PageHeader = ({ eyebrow, title, subtitle, actions }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between ka-fade">
    <div>
      {eyebrow && <div className="ka-eyebrow mb-2">{eyebrow}</div>}
      <h1 data-testid="page-title" className="text-3xl font-extrabold text-slate-900 sm:text-4xl">{title}</h1>
      {subtitle && <p className="mt-2 max-w-2xl text-sm text-slate-600 sm:text-base">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2 no-print">{actions}</div>}
  </div>
);

const TONES = {
  done: "bg-emerald-100 text-emerald-800 border-emerald-200",
  closed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  ok: "bg-emerald-100 text-emerald-800 border-emerald-200",
  due: "bg-sky-50 text-sky-700 border-sky-200",
  open: "bg-rose-50 text-rose-700 border-rose-200",
  overdue: "bg-rose-100 text-rose-800 border-rose-300",
  exception: "bg-amber-100 text-amber-900 border-amber-300",
  action_taken: "bg-amber-50 text-amber-800 border-amber-200",
  awaiting_review: "bg-sky-50 text-sky-700 border-sky-200",
  high: "bg-rose-100 text-rose-800 border-rose-300",
  medium: "bg-amber-50 text-amber-800 border-amber-200",
  low: "bg-slate-100 text-slate-700 border-slate-200",
  pending_sync: "bg-sky-100 text-sky-800 border-sky-300",
};

export const Pill = ({ status, label, className, testid }) => (
  <span data-testid={testid} className={cn("inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize", TONES[status] || "bg-slate-100 text-slate-700 border-slate-200", className)}>
    {status === "overdue" && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-rose-600" />}
    {label || String(status).replace(/_/g, " ")}
  </span>
);

export const Card = ({ className, children, ...p }) => (
  <div className={cn("ka-card p-5 sm:p-6", className)} {...p}>{children}</div>
);

export const Field = ({ label, hint, children, className }) => (
  <label className={cn("block", className)}>
    <span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
  </label>
);

export const Empty = ({ icon: Icon, title, text, testid }) => (
  <div data-testid={testid} className="flex flex-col items-start gap-2 rounded-2xl border border-dashed border-slate-300 bg-white/60 p-8">
    {Icon && <Icon className="h-6 w-6 text-slate-400" />}
    <div className="font-display text-lg font-bold text-slate-800">{title}</div>
    {text && <p className="text-sm text-slate-500">{text}</p>}
  </div>
);

export const Stat = ({ label, value, tone = "slate", testid, sub }) => {
  const tones = { slate: "text-slate-900", rose: "text-rose-600", amber: "text-amber-600", emerald: "text-emerald-600" };
  return (
    <div className="ka-card p-5">
      <div className="ka-eyebrow">{label}</div>
      <div data-testid={testid} className={cn("mt-2 font-display text-4xl font-extrabold", tones[tone])}>{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
};

export const Table = ({ children, testid }) => (
  <div className="ka-card overflow-x-auto">
    <table data-testid={testid} className="w-full min-w-[640px] text-sm">{children}</table>
  </div>
);
export const Th = ({ children, className }) => <th className={cn("border-b border-slate-200 bg-slate-50/80 px-4 py-3 text-left font-mono text-[11px] uppercase tracking-wider text-slate-500", className)}>{children}</th>;
export const Td = ({ children, className }) => <td className={cn("border-b border-slate-100 px-4 py-3 align-top text-slate-700", className)}>{children}</td>;
